import { create } from 'zustand';
import type { PlanId, Entitlements, Limits, Usage, Subscription, SubscriptionPlan } from '../types';
import {
  purchaseSubscription,
  restorePurchases,
  isNativePlatform,
} from '../services/subscription.service';
import api from '../services/api';
import { publishCapabilities, readCapabilities } from './capabilities-sync';
import { spendableCredits } from '../utils/spendable-credits';
import {
  beginSessionFlow,
  createReadLane,
  registerUserScopedReset,
  withOrderedSession,
} from './session-read';
import { getAxiosErrorMessage } from '../utils/error';

// ============================================
// Type Definitions
// ============================================

/** A store purchase or restore. A cancel is neither a success nor an error. */
export interface StoreActionResult {
  success: boolean;
  error?: string;
  cancelled?: true;
}

interface SubscriptionState {
  plan: Record<string, unknown> | null;
  planId: PlanId;
  entitlements: Entitlements;
  limits: Limits;
  usage: Usage | null;
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  products: SubscriptionPlan[];
  isPurchasing: boolean;
  /** A restore flow is in flight (its own flag: isLoading belongs to the plan reads). */
  isRestoring: boolean;
}

interface SubscriptionActions {
  /**
   * 'applied': this answer is on screen. 'superseded': a newer read of the same
   * session already landed. 'ended': the answer belongs to a session that has
   * since ended (#940) and nothing was written; the caller must skip every
   * follow-up (toast, callback). 'failed': the request failed.
   */
  fetchSubscription: () => Promise<FetchSubscriptionOutcome>;
  hasEntitlement: (feature: string) => boolean;
  canUseTokens: (bucket: string, amount?: number) => boolean;
  getRemainingTokens: (bucket: string) => number;
  getUsagePercentage: (bucket: string) => number;
  canUseCredits: (amount?: number) => boolean;
  getRemainingCredits: () => number;
  getCreditsPercentage: () => number;
  isPremium: () => boolean;
  isElite: () => boolean;
  isPaidPlan: () => boolean;
  isFree: () => boolean;
  reset: () => void;
  clearError: () => void;
  /** `cancelled`: the user closed the store sheet; show nothing. */
  purchase: (productId: string) => Promise<StoreActionResult>;
  restore: () => Promise<StoreActionResult>;
}

type SubscriptionStore = SubscriptionState & SubscriptionActions;

// ============================================
// Store Implementation
// ============================================

/**
 * The plan reads' lane (session-read.ts withOrderedSession): newest issued
 * wins for EVERY outcome. An older read that resolves, rejects or throws after
 * a newer one was issued touches nothing: not the plan fields, not the error,
 * not the loading flag (Codex r7 on #940: failures used to slip past the
 * ordering that successes had).
 */
const planReads = createReadLane();

export type FetchSubscriptionOutcome = 'applied' | 'superseded' | 'ended' | 'failed';

export const useSubscriptionStore = create<SubscriptionStore>((set, get) => ({
  // State
  plan: null,
  planId: 'free',
  entitlements: {},
  limits: {},
  usage: null,
  subscription: null,
  isLoading: false,
  error: null,

  // In-app purchase state
  products: [],
  isPurchasing: false,
  isRestoring: false,

  // Actions
  fetchSubscription: async () => {
    set({ isLoading: true, error: null });
    // Session-bound AND ordered: an answer for a session that has since ended
    // is another account's; one a newer read has superseded is out of date.
    // Either way, success or failure, it writes nothing.
    const outcome = await withOrderedSession(planReads, () => api.get('/subscription'));
    if (outcome.status === 'ended') return 'ended';
    if (outcome.status === 'superseded') return 'superseded';
    if (outcome.status === 'failed') {
      const { error } = outcome;
      set({
        error: getAxiosErrorMessage(error, 'Unknown error'),
        isLoading: false,
      });
      return 'failed';
    }
    const { read } = outcome;
    try {
      const data = outcome.value.data;

      set({
        planId: data.plan_id,
        plan: data,
        entitlements: data.entitlements ?? {},
        limits: data.limits ?? {},
        usage: data.usage,
        subscription: data.subscription,
        isLoading: false,
      });

      // Every plan change in the app ends in this fetch, so this is the ONE
      // place the auth user's capability map follows the plan.
      const capabilities = readCapabilities(data.capabilities);
      if (capabilities) publishCapabilities(capabilities, read);
      return 'applied';
    } catch (error) {
      // A malformed answer from the current session.
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false,
      });
      return 'failed';
    }
  },

  hasEntitlement: (feature: string) => {
    const entitlements = get().entitlements;
    return entitlements?.[feature] === true;
  },

  canUseTokens: (bucket: string, amount = 0) => {
    const usage = get().usage;
    if (!usage || !usage.tokens || !usage.tokens[bucket]) {
      return false;
    }

    const tokenBucket = usage.tokens[bucket];
    if (!tokenBucket) return false;

    const { used, limit } = tokenBucket;
    return used + amount <= limit;
  },

  getRemainingTokens: (bucket: string) => {
    const usage = get().usage;
    if (!usage || !usage.tokens || !usage.tokens[bucket]) {
      return 0;
    }

    return usage.tokens[bucket]?.remaining ?? 0;
  },

  getUsagePercentage: (bucket: string) => {
    const usage = get().usage;
    if (!usage || !usage.tokens || !usage.tokens[bucket]) {
      return 0;
    }

    const tokenBucket = usage.tokens[bucket];
    if (!tokenBucket) return 0;

    const { used, limit } = tokenBucket;
    if (limit === 0) return 0;
    return Math.round((used / limit) * 100);
  },

  canUseCredits: (amount = 0) => {
    const spendable = spendableCredits(get().usage);
    if (spendable === undefined || spendable <= 0) return false;
    return amount <= spendable;
  },

  getRemainingCredits: () => spendableCredits(get().usage) ?? 0,

  getCreditsPercentage: () => {
    const usage = get().usage;
    if (!usage?.credits) return 0;
    const { used, limit } = usage.credits;
    if (limit === 0) return 0;
    return Math.round((used / limit) * 100);
  },

  isPremium: () => {
    return get().planId === 'premium';
  },

  isElite: () => {
    return get().planId === 'elite';
  },

  isPaidPlan: () => {
    const planId = get().planId;
    return planId === 'premium' || planId === 'elite';
  },

  isFree: () => {
    return get().planId === 'free';
  },

  reset: () => {
    set({
      plan: null,
      planId: 'free',
      entitlements: {},
      limits: {},
      usage: null,
      subscription: null,
      isLoading: false,
      error: null,
      // Every busy flag: a session change leaves none of the previous
      // account's flows looking in progress (#940 r8).
      isPurchasing: false,
      isRestoring: false,
    });
  },

  clearError: () => {
    set({ error: null });
  },

  purchase: async (productId: string) => {
    // A user-initiated flow: its session is captured HERE, before the first
    // await, and every later step runs under it (#940 r7). A switch during the
    // store sheet must not report success for, or refresh, the next account.
    const flow = beginSessionFlow();
    set({ isPurchasing: true, error: null });
    // isPurchasing is cleared in `finally`: no outcome, 'ended' included, can
    // leave it set (#940 r8).
    try {
      // Check if we're on a native platform
      if (!isNativePlatform()) {
        set({
          error:
            'In-app purchases are only available on mobile devices. Please use the web checkout.',
        });
        return { success: false, error: 'Not on native platform' };
      }

      // Call the subscription service to initiate purchase
      const bought = await flow.step(() => purchaseSubscription(productId));
      if (bought.status === 'ended') return { success: false };
      if (bought.status === 'failed') {
        const errorMessage = bought.error instanceof Error ? bought.error.message : 'Unknown error';
        console.error('Purchase failed:', errorMessage);
        set({ error: errorMessage });
        return { success: false, error: errorMessage };
      }

      // The user closed the store sheet: not a purchase and not an error, so
      // nothing is shown and the picker stays open.
      if (bought.value === 'cancelled') return { success: false, cancelled: true };

      // If we get here, the purchase was initiated successfully. The actual
      // subscription update happens via the 'verified' callback in the service.
      // Refresh subscription data after a short delay to allow backend
      // validation — still under the session that bought it.
      void flow
        .step(() => new Promise<void>((done) => setTimeout(done, 2000)))
        .then((waited) => {
          if (waited.status === 'ok') void get().fetchSubscription();
        });

      return { success: true };
    } finally {
      set({ isPurchasing: false });
    }
  },

  restore: async () => {
    // Captured before the first await (the native store dialog), see purchase.
    const flow = beginSessionFlow();
    set({ isRestoring: true, error: null });
    // isRestoring is cleared in `finally`, whatever the outcome (#940 r8).
    // The refresh below manages isLoading itself, through the plan-read lane.
    try {
      // Check if we're on a native platform
      if (!isNativePlatform()) {
        set({ error: 'Restore purchases is only available on mobile devices.' });
        return { success: false, error: 'Not on native platform' };
      }

      // Call the subscription service to restore purchases
      const restored = await flow.step(() => restorePurchases());
      if (restored.status === 'ended') return { success: false };
      if (restored.status === 'failed') {
        const errorMessage =
          restored.error instanceof Error ? restored.error.message : 'Unknown error';
        console.error('Restore failed:', errorMessage);
        set({ error: errorMessage });
        return { success: false, error: errorMessage };
      }

      // Closed by the user: nothing restored, nothing to say.
      if (restored.value === 'cancelled') return { success: false, cancelled: true };

      // Refresh subscription data, under the same session. 'ended': the
      // session that asked is gone, so report nothing for whoever is signed in.
      const refreshed = await flow.step(() => get().fetchSubscription());
      if (refreshed.status !== 'ok' || refreshed.value === 'ended') return { success: false };

      return { success: true };
    } finally {
      set({ isRestoring: false });
    }
  },
}));

// Per-user: reset synchronously on every session change and on entering the
// guest tour, with every per-user store (session-read.ts). It was reset by name
// from auth.store's session-change path, a second mechanism the guest-tour
// entry did not run (backlog c-029/c-030 sweep).
registerUserScopedReset(() => useSubscriptionStore.getState().reset());
